/**
 * @file: analytic-events.ts
 * @copyright: 2021, popshop live, inc.
 *
 * Analytic events functions in this file are generated by a tool from the
 * definition at https://github.com/popshoplive/psl-analytics-events.
 * Don't make changes to this file, otherwise all changes you made will be
 * overwritten the next time this file is automatically generated again.
 */

export type AnalyticEventProps = {
  [key: string]: any;
  [index: number]: any;
};

export type AnalyticEventListener = (
  event: string,
  props?: AnalyticEventProps,
) => void;

export interface AnalyticEventEmitter {
  emit(event: string, props?: AnalyticEventProps): void;
  addEventListener(listener: AnalyticEventListener): () => void;
  removeEventListener(listener: AnalyticEventListener): void;
}

/**
 * Class that provides methods for psl analytic event tracking.
 * @abstract
 *
 * @description
 * This class is abstract thus is not intended be used directly by creating
 * instance of this class and call event tracking methods on it.
 *
 * Instead, either use the singleton instance `AnalyticEvents.default` that
 * provides the default implementation in a event driven manner, for example:
 *
 * @example
 * ```typescript
 * // register event listener that handles emitted events
 * AnalyticEvents.default.addEventListener((event, props) => {
 *  // call other event library method to do the real event dispatch
 *  someEventLib.track(event, props);
 * });
 * // call instance methods on the default instance to emit events
 * AnalyticEvents.default.liveShowBroadcastStarted({
 *   showId: '...',
 *   agoraAccount: '...'
 * });
 * ```
 * Or, you can implement this class yourself by implementing the `track` method,
 * for example,
 *
 * @example
 * ```typescript
 * class MyAnalyticEventsImpl extends AnalyticEvents {
 *   track(event: string, props: AnalyticEventProps) {
 *     // call other event library method to do the real event dispatch
 *     someEventLib.track(event, props);
 *   }
 * }
 * // create instance of your implementation
 * const analyticEvents = new MyAnalyticEventsImpl();
 * // and then call instance event tracking methods on it
 * analyticEvents.liveShowBroadcastStarted({
 *   showId: '...',
 *   agoraAccount: '...'
 * });
 * ```
 * The default implementation is recommended to use unless custom implementation
 * is necessary in rare cases.
 */
export abstract class AnalyticEvents<T = unknown> {
  //#region Default Implementation
  /** @readonly instance that implements the default analytic event tracking */
  static readonly default = new (class DefaultAnalyticEventsImpl
    extends AnalyticEvents
    implements AnalyticEventEmitter
  {
    private listeners: AnalyticEventListener[] = [];

    /**
     * Method that can be called to emit custom analytic event that is not
     * exposed via the automatically generated tracking methods.
     * @param event the name of the custom event to emit
     * @param [props] the custom event properties
     */
    emit(event: string, props?: AnalyticEventProps): void {
      props = eventPropsConverted(props);
      for (const listener of this.listeners) {
        listener(event, props);
      }
    }

    /**
     * Add analytic event listener and implement actual event forward there.
     * @param listener the event listener to add
     * @returns a function that can be called to remove the listener
     */
    addEventListener(listener: AnalyticEventListener): () => void {
      this.listeners.push(listener);
      return () => {
        this.removeEventListener(listener);
      };
    }

    /**
     * Remove the given event listener.
     * @param listener the listener to remove
     */
    removeEventListener(listener: AnalyticEventListener): void {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    }

    // the default track method implementation
    protected track(event: string, props?: AnalyticEventProps) {
      this.emit(event, props);
    }
  })();
  //#endregion

  /**
   * Derived class should implement this method to track the given events
   * with properties.
   * @param {string} event - event name
   * @param {string} [props] - event properties
   */
  protected abstract track(event: string, props?: AnalyticEventProps): T;

  //#region Order review Related Analytic Events

  /**
   * Analytic event logged on order review created
   * @param [extra] - extra properties for this event
   */
  orderReviewCreated(extra?: AnalyticEventProps) {
    return this.track('order_review_created', extra);
  }

  /**
   * Analytic event logged on order review upvoted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  orderReviewUpvoted(
    props: {
      /** action_type property */
      actionType: 'add' | 'remove';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('order_review_upvoted', { ...props, ...extra });
  }

  //#endregion

  //#region Open seller dashboard pressed Related Analytic Events

  /**
   * Analytic event logged on open seller dashboard pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  openSellerDashboardPressed(
    props: {
      /** from property */
      from:
        | 'seller_settings'
        | 'web_footer'
        | 'discovery_header'
        | 'seller_application_success_page'
        | 'creator_inventory_notice_page'
        | 'psl_pay_setup_notice_page'
        | 'empty_inventory_notice_page'
        | 'storefront_inventory_page'
        | 'commentchat_setup_flow';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('open_seller_dashboard_pressed', { ...props, ...extra });
  }

  //#endregion

  //#region App Related Analytic Events

  /**
   * The app is opened by a user from all entry point
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  appLaunched(
    props: {
      /** number of application launches */
      counter: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('app_launched', { ...props, ...extra });
  }

  /**
   * The app has been opened by tapping on a push notification
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  appPushNotificationPressed(
    props: {
      /** type of notification */
      type:
        | 'global_message'
        | 'order_review'
        | 'order_review_reply'
        | 'order_review_remind'
        | 'order_review_upvoted'
        | 'order_status_change'
        | 'live_show_started'
        | 'live_show_about_to_start'
        | 'live_show_cap_off'
        | 'upcoming_show_did_create'
        | 'upcoming_show_comment'
        | 'received_send_item'
        | 'gift_recipient_addr_required'
        | 'gift_created'
        | 'coupon_assigned_to_user'
        | 'chat_mention'
        | 'follow'
        | 'badge_earned'
        | 'wave_followers_to_watch_show'
        | 'seller_application_approved'
        | 'referral_gifting_received'
        | 'custom';
      /** type of the show */
      showType?:
        | (
            | 'liveShow'
            | 'upcomingShow'
            | 'replay'
            | 'past'
            | 'event'
            | 'promotion'
            | 'seller'
          )
        | null;
      /** id of the seller */
      sellerId?: string | null;
      /** name of the seller */
      sellerName?: string | null;
      /** where the notification is presented */
      from?: ('notification_center' | 'in_app') | null;
      /** notification title that cannot be inferred from notification type */
      title?: string | null;
      /** notification message that cannot be inferred from notification type */
      message?: string | null;
      /** call to action name that cannot be inferred from notification type */
      cta?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('app_push_notification_pressed', { ...props, ...extra });
  }

  //#endregion

  //#region Onboarding Related Analytic Events

  /**
   * User clicked on the login CTA
   * @param [extra] - extra properties for this event
   */
  onboardingLoginCtaPressed(extra?: AnalyticEventProps) {
    return this.track('onboarding_login_cta_pressed', extra);
  }

  /**
   * User has tried to login/signup with one of the options
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingLoginStarted(
    props: {
      /** firebase authentication provider identifier */
      providerId: string;
      /** in which context the user logged in (phone, facebook, apple) */
      scene?: string | null;
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_login_started', { ...props, ...extra });
  }

  /**
   * User has successfully logged in
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingLoginSuccess(
    props: {
      /** firebase authentication provider identifier */
      providerId: string;
      /** in which context the user logged in (phone, facebook, apple) */
      scene?: string | null;
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_login_success', { ...props, ...extra });
  }

  /**
   * User's account has been created
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupAccountCreatedOnboardingStarted(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** firebase authentication user id */
      userId?: string | null;
      /** firebase authentication provider identifier */
      providerId?: string | null;
      /** in which context the user signed up (phone, facebook, apple) */
      scene?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_account_created_onboarding_started', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has filled in their name and phone information
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupFinishFillNameAndPhone(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_finish_fill_name_and_phone', {
      ...props,
      ...extra,
    });
  }

  /**
   * User is picking their avatar picture
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupAvatarPhotoPicked(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the url of the avatar */
      avatarUrl: string;
      /** type property */
      type: 'photo_upload' | 'take_photo';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_avatar_photo_picked', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has submitted their avatar
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupAvatarPhotoSubmitted(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** type property */
      type: 'picked' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_avatar_photo_submitted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Invitation code used during onboarding
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupInvitationCode(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the referral code used by the user */
      code: string;
      /** type property */
      type: 'code' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_invitation_code', {
      ...props,
      ...extra,
    });
  }

  /**
   * User finished the categories selection step
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupCategoriesSelected(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the number of categories that the user has selected */
      categoryCount: number;
      /** type property */
      type: 'picked' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_categories_selected', {
      ...props,
      ...extra,
    });
  }

  /**
   * User followed a store in stores following step
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupStoresFollowed(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the store id of the store that the user has followed */
      storeId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_stores_followed', {
      ...props,
      ...extra,
    });
  }

  /**
   * User finished the stores follow step
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupStoresFollowedFinished(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the number of stores that the user has followed */
      storeCount: number;
      /** type property */
      type: 'picked' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_stores_followed_finished', {
      ...props,
      ...extra,
    });
  }

  /**
   * Tracked when user completes onboarding flow
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  onboardingSignupCompleted(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('onboarding_signup_completed', { ...props, ...extra });
  }

  //#endregion

  //#region New onboarding Related Analytic Events

  /**
   * when the new onboarding flow has been resumed from previous interrupted one
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingResumed(
    props: {
      /** which onboarding phase it is resumed from */
      fromPhase: 'auth' | 'basic-info' | 'extra-info';
      /** which onboarding step it's resumed from */
      fromStep:
        | 'phone'
        | 'sms-code-verify'
        | 'connect-phone'
        | 'connect-phone-sms-code-verify'
        | 'name-info'
        | 'email'
        | 'username';
      /** which onboarding phase it is resumed to */
      toPhase: 'auth' | 'basic-info' | 'extra-info' | 'completed';
      /** which onboarding step it is resumed to */
      toStep?:
        | ('phone' | 'connect-phone' | 'name-info' | 'email' | 'username')
        | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_resumed', { ...props, ...extra });
  }

  /**
   * User clicked on the login CTA
   * @param [extra] - extra properties for this event
   */
  newOnboardingLoginCtaPressed(extra?: AnalyticEventProps) {
    return this.track('new_onboarding_login_cta_pressed', extra);
  }

  /**
   * User has tried to login/signup with one of the options
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingLoginStarted(
    props: {
      /** firebase authentication provider identifier */
      providerId: string;
      /** in which context the user logged in (phone, facebook, apple) */
      scene?: string | null;
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_login_started', { ...props, ...extra });
  }

  /**
   * User has successfully logged in
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingLoginSuccess(
    props: {
      /** firebase authentication provider identifier */
      providerId: string;
      /** in which context the user logged in (phone, facebook, apple) */
      scene?: string | null;
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_login_success', { ...props, ...extra });
  }

  /**
   * User has started to connect/confirm their phone number
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingLoginConnectPhoneStarted(
    props: {
      /** source filebase authentication provider identifier */
      sourceProviderId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_login_connect_phone_started', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has finished to connect/confirm their phone number
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingLoginConnectPhoneFinished(
    props: {
      /** source filebase authentication provider identifier */
      sourceProviderId: string;
      /** user id of the final user account merged from source provider */
      userId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_login_connect_phone_finished', {
      ...props,
      ...extra,
    });
  }

  /**
   * User's account has been created
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupAccountCreatedOnboardingStarted(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** firebase authentication user id */
      userId?: string | null;
      /** firebase authentication provider identifier */
      providerId?: string | null;
      /** in which context the user signed up (phone, facebook, apple) */
      scene?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'new_onboarding_signup_account_created_onboarding_started',
      { ...props, ...extra },
    );
  }

  /**
   * User has filled in their name information
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupFinishFillName(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_finish_fill_name', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has filled in their email information
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupFinishFillEmail(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_finish_fill_email', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has filled in their username information
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupFinishFillUsername(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_finish_fill_username', {
      ...props,
      ...extra,
    });
  }

  /**
   * User profile has been setup
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupFinishSetupProfile(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_finish_setup_profile', {
      ...props,
      ...extra,
    });
  }

  /**
   * User is picking their avatar picture
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupAvatarPhotoPicked(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the url of the avatar */
      avatarUrl: string;
      /** type property */
      type: 'photo_upload' | 'take_photo';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_avatar_photo_picked', {
      ...props,
      ...extra,
    });
  }

  /**
   * User has submitted their avatar
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupAvatarPhotoSubmitted(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** type property */
      type: 'picked' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_avatar_photo_submitted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Invitation code used during onboarding
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupInvitationCode(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** the referral code used by the user */
      code: string;
      /** type property */
      type: 'code' | 'skipped';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_invitation_code', {
      ...props,
      ...extra,
    });
  }

  /**
   * Tracked when user completes onboarding flow
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  newOnboardingSignupCompleted(
    props: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** type property */
      type: 'normal' | 'resumed';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('new_onboarding_signup_completed', {
      ...props,
      ...extra,
    });
  }

  //#endregion

  //#region Seller profile Related Analytic Events

  /**
   * Analytic event logged on seller profile open
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerProfileOpen(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** type of the show */
      showType?:
        | (
            | 'liveShow'
            | 'upcomingShow'
            | 'replay'
            | 'past'
            | 'event'
            | 'promotion'
            | 'seller'
          )
        | null;
      /** scene property */
      scene?:
        | (
            | 'search'
            | 'action_menu'
            | 'upcoming_show_action_menu'
            | 'showcase_live_host_username'
            | 'showcase_live_guest_username'
            | 'discovery_product_page'
            | 'featured_see_all_page'
            | 'unbundled_see_all_page'
            | 'unbundled_see_all_recommended_seller_page'
            | 'discovery_recommended_seller'
            | 'discovery_recommended_seller_see_all'
            | 'account_search_following_list'
            | 'account_search_featured_store'
            | 'account_search_result'
            | ''
          )
        | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_profile_open', { ...props, ...extra });
  }

  //#endregion

  //#region Discovery Related Analytic Events

  /**
   * Analytic event logged on discovery upcoming show added to watchlist
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  discoveryUpcomingShowAddedToWatchlist(
    props: {
      /** trigger_location property */
      triggerLocation:
        | 'featured'
        | 'discovery'
        | 'events'
        | 'unbundled'
        | 'recently_added'
        | 'profile';
      /** bundled_row_name property */
      bundledRowName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('discovery_upcoming_show_added_to_watchlist', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on discovery upcoming show removed from watchlist
   * @param [extra] - extra properties for this event
   */
  discoveryUpcomingShowRemovedFromWatchlist(extra?: AnalyticEventProps) {
    return this.track('discovery_upcoming_show_removed_from_watchlist', extra);
  }

  /**
   * product clicked
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  discoveryProductRowPressed(
    props: {
      /** type property */
      type: '' | 'live' | 'upcoming' | 'store_fav' | '';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('discovery_product_row_pressed', { ...props, ...extra });
  }

  /**
   * Analytic event logged on discovery featured event pressed
   * @param [extra] - extra properties for this event
   */
  discoveryFeaturedEventPressed(extra?: AnalyticEventProps) {
    return this.track('discovery_featured_event_pressed', extra);
  }

  /**
   * Analytic event logged on discovery new to popshop row pressed
   * @param [extra] - extra properties for this event
   */
  discoveryNewToPopshopRowPressed(extra?: AnalyticEventProps) {
    return this.track('discovery_new_to_popshop_row_pressed', extra);
  }

  /**
   * Analytic event logged on discovery live show pressed
   * @param [extra] - extra properties for this event
   */
  discoveryLiveShowPressed(extra?: AnalyticEventProps) {
    return this.track('discovery_live_show_pressed', extra);
  }

  //#endregion

  //#region Showcase Related Analytic Events

  /**
   * Login reminder dialog shown in the showcase page
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLoginReminderShown(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_login_reminder_shown', { ...props, ...extra });
  }

  /**
   * User logged in by the login reminder
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLoginReminderActionTaken(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_login_reminder_action_taken', {
      ...props,
      ...extra,
    });
  }

  /**
   * "Don't forget to follow Xxxx" notification be shown in the comments list
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseFollowUpsellInCommentShown(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_follow_upsell_in_comment_shown', {
      ...props,
      ...extra,
    });
  }

  /**
   * User followed the seller by the notification
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseFollowUpsellInCommentActionTaken(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_follow_upsell_in_comment_action_taken', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase show exited
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseShowExited(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** exit_type property */
      exitType: 'collapse_button' | 'swipe_down' | 'swipe_side';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_show_exited', { ...props, ...extra });
  }

  /**
   * Analytic event logged on showcase mute pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseMutePressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** mute_status property */
      muteStatus: 'muted' | 'unmuted';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_mute_pressed', { ...props, ...extra });
  }

  /**
   * Analytic event logged on showcase promo bin open
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcasePromoBinOpen(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_promo_bin_open', { ...props, ...extra });
  }

  /**
   * Analytic event logged on showcase promo bin code pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcasePromoBinCodePressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_promo_bin_code_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase promo bin code redeemed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcasePromoBinCodeRedeemed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_promo_bin_code_redeemed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase promo bin my promo pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcasePromoBinMyPromoPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_promo_bin_my_promo_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase promo bin my promo code pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcasePromoBinMyPromoCodePressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_promo_bin_my_promo_code_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase lightning bolt action menu open
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLightningBoltActionMenuOpen(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_lightning_bolt_action_menu_open', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase lightning bolt animation toggle
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLightningBoltAnimationToggle(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** status property */
      status: 'on' | 'off';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_lightning_bolt_animation_toggle', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase lightning bolt my likes toggle
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLightningBoltMyLikesToggle(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** status property */
      status: 'on' | 'off';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_lightning_bolt_my_likes_toggle', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase lightning bolt hide comment toggle
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseLightningBoltHideCommentToggle(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** status property */
      status: 'on' | 'off';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_lightning_bolt_hide_comment_toggle', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase feature product pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseFeatureProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_feature_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * User pressed the "See All" button the see the catalog
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSeeAllProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_see_all_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction collapsed bid pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionCollapsedBidPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_collapsed_bid_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction collapsed comments pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionCollapsedCommentsPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_collapsed_comments_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * User opened the auction detail page
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * User pressed the increment button
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidQuickIncrementPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_quick_increment_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * User pressed the increment customization button
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidQuickIncrementChanged(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** increment_chosen property */
      incrementChosen: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_quick_increment_changed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction bid default pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidDefaultPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_default_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller pressed the auction list button in the showcase actions list
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidMenuPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_menu_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction bid menu bid options
   * pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidMenuBidOptionsPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** options_chosen property */
      optionsChosen: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_menu_bid_options_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction bid manual bid entered
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidManualBidEntered(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_manual_bid_entered', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction bid manual bid pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionBidManualBidPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** price property */
      price: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_bid_manual_bid_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction confirmation nevermind
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionConfirmationNevermind(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_confirmation_nevermind', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction confirmation yes
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionConfirmationYes(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_confirmation_yes', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction won checkout flow started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionWonCheckoutFlowStarted(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_won_checkout_flow_started', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction won checkout flow finished
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionWonCheckoutFlowFinished(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_won_checkout_flow_finished', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase buyer auction won checkout flow declined
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseBuyerAuctionWonCheckoutFlowDeclined(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_buyer_auction_won_checkout_flow_declined', {
      ...props,
      ...extra,
    });
  }

  /**
   * "This show is over" modal be shown
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseShowOverShown(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_show_over_shown', { ...props, ...extra });
  }

  /**
   * show is over and auto switches the user to the next show
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseShowOverAutoSwitched(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_show_over_auto_switched', {
      ...props,
      ...extra,
    });
  }

  /**
   * show is over and user manually switches to the next show
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseShowOverManuallySwitched(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_show_over_manually_switched', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller video call pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller video call user search
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallUserSearch(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_user_search', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller video call random picker pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallRandomPickerPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_random_picker_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller video call start pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallStartPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_start_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Audience accepted two way stream call
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallRequestGuestAccepted(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
      /** id of the guest user being called */
      guestId: string;
      /** nick name of the guest user being called */
      guestNick: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_request_guest_accepted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Audience declined two way stream call
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerVideoCallRequestGuestDeclined(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
      /** id of the guest user being called */
      guestId: string;
      /** nick name of the guest user being called */
      guestNick: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_video_call_request_guest_declined', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller close button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCloseButtonPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_close_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller camera flip
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCameraFlip(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** orientation property */
      orientation: 'front' | 'back';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_camera_flip', { ...props, ...extra });
  }

  /**
   * Analytic event logged on showcase seller mic mute pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMicMutePressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** mute_status property */
      muteStatus: 'muted' | 'unmuted';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_mic_mute_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller selected specific auction to open the auction manage page
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller auction previous auction item
   * pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionPreviousAuctionItemPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** auction_status property */
      auctionStatus: 'ongoing' | 'closed' | 'cancelled';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_previous_auction_item_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller started a new auction creation flow
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionStartNewAuction(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_start_new_auction', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller auction new auction item pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionNewAuctionItemPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_new_auction_item_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller confirmed the "start auction" preview page
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionStarted(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** edited_price property */
      editedPrice: boolean;
      /** price_changed property */
      priceChanged: 'lower' | 'higher';
      /** added_timer property */
      addedTimer: boolean;
      /** starting_time property */
      startingTime: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_started', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller cancelled an auction
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionCancelPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_cancel_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller finished an auction
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionFinishedPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_finished_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller removed a bit from an auction
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionBidRemoved(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_bid_removed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Seller opened a live auction detail page
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerAuctionLiveProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_auction_live_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller more button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_more_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller more button paused pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonPausedPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_more_button_paused_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller more button paused confirmed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonPausedConfirmed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** has_notes property */
      hasNotes: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_more_button_paused_confirmed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller more button settings pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonSettingsPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_more_button_settings_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller more button settings ai noise
   * deduction pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonSettingsAiNoiseDeductionPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** toggle property */
      toggle: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'showcase_seller_more_button_settings_ai_noise_deduction_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on showcase seller more button settings automatic
   * face detection pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonSettingsAutomaticFaceDetectionPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** toggle property */
      toggle: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'showcase_seller_more_button_settings_automatic_face_detection_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on showcase seller more button settings zoom changed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerMoreButtonSettingsZoomChanged(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** zoom_amount property */
      zoomAmount: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_more_button_settings_zoom_changed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog default add pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogDefaultAddPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_default_add_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog feature product pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogFeatureProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_feature_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog add product button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogAddProductButtonPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_add_product_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog create new pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogCreateNewPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_create_new_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog create new added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogCreateNewAdded(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** discount property */
      discount?: number | null;
      /** add_variant property */
      addVariant: boolean;
      /** sku_added property */
      skuAdded?: boolean | null;
      /** flat_shipping property */
      flatShipping?: boolean | null;
      /** max_purchase property */
      maxPurchase?: number | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_create_new_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog add from inventory pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogAddFromInventoryPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_add_from_inventory_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog add from inventory added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogAddFromInventoryAdded(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** amount property */
      amount: number;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_add_from_inventory_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product photo uploaded
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductPhotoUploaded(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_photo_uploaded', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product price changed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductPriceChanged(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_price_changed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product discount price
   * added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductDiscountPriceAdded(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_discount_price_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product amount adjusted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductAmountAdjusted(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_amount_adjusted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product added variant
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductAddedVariant(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_added_variant', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product advance setting
   * saved
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductAdvanceSettingSaved(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
      /** flat_shipping_status property */
      flatShippingStatus: boolean;
      /** max_purchase_status property */
      maxPurchaseStatus: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_advance_setting_saved', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog product update saved
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogProductUpdateSaved(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_product_update_saved', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog menu pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogMenuPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_menu_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog menu featured
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogMenuFeatured(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_menu_featured', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog menu view
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogMenuView(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_menu_view', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller catalog menu remove from show
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerCatalogMenuRemoveFromShow(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_catalog_menu_remove_from_show', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller send gift pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerSendGiftPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_send_gift_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller send gift product pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerSendGiftProductPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_send_gift_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller send gift viewer pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerSendGiftViewerPressed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_send_gift_viewer_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller send gift checkout start
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerSendGiftCheckoutStart(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_send_gift_checkout_start', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on showcase seller send gift checkout confirmed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showcaseSellerSendGiftCheckoutConfirmed(
    props: {
      /** type of the show */
      showType:
        | 'liveShow'
        | 'upcomingShow'
        | 'replay'
        | 'past'
        | 'event'
        | 'promotion'
        | 'seller';
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('showcase_seller_send_gift_checkout_confirmed', {
      ...props,
      ...extra,
    });
  }

  //#endregion

  //#region Seller application Related Analytic Events

  /**
   * Analytic event logged on seller application get started page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationGetStartedPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_get_started_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application account info page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationAccountInfoPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_account_info_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application code verify page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationCodeVerifyPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_code_verify_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application personal info page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationPersonalInfoPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_personal_info_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application store info page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationStoreInfoPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_store_info_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application success page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationSuccessPageViewed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_success_page_viewed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application get started button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationGetStartedButtonPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_get_started_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application submit button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationSubmitButtonPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_submit_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application goto seller dashboard pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationGotoSellerDashboardPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_goto_seller_dashboard_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application done button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationDoneButtonPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_done_button_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application product source selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationProductSourceSelected(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
      /** user selected produce source */
      type: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_product_source_selected', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application allow sms selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationAllowSmsSelected(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
      /** if user opt-in sms notifications */
      checked: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_allow_sms_selected', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application terms and conditions link
   * pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationTermsAndConditionsLinkPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_terms_and_conditions_link_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application privacy policy link pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationPrivacyPolicyLinkPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_privacy_policy_link_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application dropship seller terms link
   * pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationDropshipSellerTermsLinkPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_dropship_seller_terms_link_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller application mutual arbitration agreement
   * link pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationMutualArbitrationAgreementLinkPressed(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'seller_application_mutual_arbitration_agreement_link_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on seller application open
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationOpen(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_open', { ...props, ...extra });
  }

  /**
   * Analytic event logged on seller application aborted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationAborted(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_aborted', { ...props, ...extra });
  }

  /**
   * Analytic event logged on seller application submitted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerApplicationSubmitted(
    props: {
      /** seller application platform identifier */
      platform: 'ios' | 'android' | 'windows' | 'web' | 'macos';
      /** seller type */
      sellerType: 'creator' | 'business';
      /** seller code */
      sellerCode: string;
      /** seller full name */
      sellerName: string;
      /** seller email address */
      sellerEmail: string;
      /** seller mobile phone number */
      sellerMobile: string;
      /** social urls of seller */
      sellerSocialUrls?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_application_submitted', { ...props, ...extra });
  }

  //#endregion

  //#region Show management Related Analytic Events

  /**
   * Analytic event logged on show management start show now pressed
   * @param [extra] - extra properties for this event
   */
  showManagementStartShowNowPressed(extra?: AnalyticEventProps) {
    return this.track('show_management_start_show_now_pressed', extra);
  }

  /**
   * Analytic event logged on show management schedule show pressed
   * @param [extra] - extra properties for this event
   */
  showManagementScheduleShowPressed(extra?: AnalyticEventProps) {
    return this.track('show_management_schedule_show_pressed', extra);
  }

  /**
   * Analytic event logged on show management upcoming show cancel
   * @param [extra] - extra properties for this event
   */
  showManagementUpcomingShowCancel(extra?: AnalyticEventProps) {
    return this.track('show_management_upcoming_show_cancel', extra);
  }

  /**
   * Analytic event logged on show management upcoming show delete
   * @param [extra] - extra properties for this event
   */
  showManagementUpcomingShowDelete(extra?: AnalyticEventProps) {
    return this.track('show_management_upcoming_show_delete', extra);
  }

  /**
   * Analytic event logged on show management upcoming show camera changed
   * @param [extra] - extra properties for this event
   */
  showManagementUpcomingShowCameraChanged(extra?: AnalyticEventProps) {
    return this.track('show_management_upcoming_show_camera_changed', extra);
  }

  /**
   * Analytic event logged on show management upcoming show start now confirmed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showManagementUpcomingShowStartNowConfirmed(
    props: {
      /** show_mode property */
      showMode: 'shipping' | 'pickup_and_shipping' | 'pickup_only';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('show_management_upcoming_show_start_now_confirmed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on show management upcoming show schedule show
   * confirmed
   * @param [extra] - extra properties for this event
   */
  showManagementUpcomingShowScheduleShowConfirmed(extra?: AnalyticEventProps) {
    return this.track(
      'show_management_upcoming_show_schedule_show_confirmed',
      extra,
    );
  }

  /**
   * Analytic event logged on show management greenroom started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  showManagementGreenroomStarted(
    props: {
      /** show_mode property */
      showMode: 'shipping' | 'pickup_and_shipping' | 'pickup_only';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('show_management_greenroom_started', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on show management greenroom go live
   * @param [extra] - extra properties for this event
   */
  showManagementGreenroomGoLive(extra?: AnalyticEventProps) {
    return this.track('show_management_greenroom_go_live', extra);
  }

  //#endregion

  //#region Store front management Related Analytic Events

  /**
   * Analytic event logged on store front management inventory tab pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryTabPressed(extra?: AnalyticEventProps) {
    return this.track('store_front_management_inventory_tab_pressed', extra);
  }

  /**
   * Analytic event logged on store front management inventory add product
   * pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryAddProductPressed(extra?: AnalyticEventProps) {
    return this.track(
      'store_front_management_inventory_add_product_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory add product saved
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryAddProductSaved(extra?: AnalyticEventProps) {
    return this.track(
      'store_front_management_inventory_add_product_saved',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory store favourites
   * pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryStoreFavouritesPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_store_favourites_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory store favourites
   * product pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryStoreFavouritesProductPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_store_favourites_product_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory store favourites
   * product topped
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryStoreFavouritesProductTopped(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_store_favourites_product_topped',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory store favourites
   * product removed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryStoreFavouritesProductRemoved(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_store_favourites_product_removed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management inventory product pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductPressed(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('store_front_management_inventory_product_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on store front management inventory product updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductUpdated(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('store_front_management_inventory_product_updated', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on store front management inventory product action
   * button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductActionButtonPressed(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_product_action_button_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on store front management inventory product featured
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductFeatured(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('store_front_management_inventory_product_featured', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on store front management inventory product delete
   * button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductDeleteButtonPressed(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_inventory_product_delete_button_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on store front management inventory product deleted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementInventoryProductDeleted(
    props: {
      /** The name of the product the seller is managing */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('store_front_management_inventory_product_deleted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on store front management sold tab pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldTabPressed(extra?: AnalyticEventProps) {
    return this.track('store_front_management_sold_tab_pressed', extra);
  }

  /**
   * Analytic event logged on store front management sold order pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderPressed(extra?: AnalyticEventProps) {
    return this.track('store_front_management_sold_order_pressed', extra);
  }

  /**
   * Analytic event logged on store front management sold order add tracking
   * button pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderAddTrackingButtonPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_add_tracking_button_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order tracking details
   * saved
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderTrackingDetailsSaved(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_tracking_details_saved',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order thank you video
   * recording button pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderThankYouVideoRecordingButtonPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_thank_you_video_recording_button_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order thank you video
   * action button pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderThankYouVideoActionButtonPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_thank_you_video_action_button_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order thank you video
   * delete button pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderThankYouVideoDeleteButtonPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_thank_you_video_delete_button_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order thank you video
   * deleted
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderThankYouVideoDeleted(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_thank_you_video_deleted',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management sold order thank you video
   * play button pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementSoldOrderThankYouVideoPlayButtonPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_sold_order_thank_you_video_play_button_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management reviews tab pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementReviewsTabPressed(extra?: AnalyticEventProps) {
    return this.track('store_front_management_reviews_tab_pressed', extra);
  }

  /**
   * Analytic event logged on store front management reviews customer reviews
   * tab pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementReviewsCustomerReviewsTabPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_reviews_customer_reviews_tab_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management reviews customer review
   * pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementReviewsCustomerReviewPressed(extra?: AnalyticEventProps) {
    return this.track(
      'store_front_management_reviews_customer_review_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management reviews sent thank you tab
   * pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementReviewsSentThankYouTabPressed(
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'store_front_management_reviews_sent_thank_you_tab_pressed',
      extra,
    );
  }

  /**
   * Analytic event logged on store front management reviews sent thank you
   * pressed
   * @param [extra] - extra properties for this event
   */
  storeFrontManagementReviewsSentThankYouPressed(extra?: AnalyticEventProps) {
    return this.track(
      'store_front_management_reviews_sent_thank_you_pressed',
      extra,
    );
  }

  //#endregion

  //#region Share Related Analytic Events

  /**
   * Analytic event logged on share bottom sheet open
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  shareBottomSheetOpen(
    props: {
      /** Where the share bottom sheet gets opened */
      triggerLocation:
        | 'live-show'
        | 'upcoming-show'
        | 'replay'
        | 'past'
        | 'storefront'
        | 'greenroom'
        | 'event'
        | 'promotion'
        | 'seller-profile'
        | 'seller-profile-self-share'
        | 'show-product'
        | 'show-product-see-more'
        | 'referral-screen'
        | 'order-review';
      /** TODO: disable anonymous user share */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_bottom_sheet_open', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share bottom sheet action
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  shareBottomSheetAction(
    props: {
      /** type property */
      type:
        | 'link'
        | 'sms'
        | 'instagram'
        | 'facebook'
        | 'twitter'
        | 'email'
        | 'more';
      /** instagram sharing type */
      shareType?: ('stories' | 'library') | null;
      /** type of media shared to instagram */
      mediaType?: ('video' | 'image') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_bottom_sheet_action', { ...props, ...extra });
  }

  //#endregion

  //#region Campaign Related Analytic Events

  /**
   * Analytic event logged on campaign web app download upsell appstore pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  campaignWebAppDownloadUpsellAppstorePressed(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('campaign_web_app_download_upsell_appstore_pressed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on campaign web app download upsell googleplay
   * pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  campaignWebAppDownloadUpsellGoogleplayPressed(
    props?: {
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('campaign_web_app_download_upsell_googleplay_pressed', {
      ...props,
      ...extra,
    });
  }

  //#endregion

  //#region Share play Related Analytic Events

  /**
   * Analytic event logged on share play session start
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlaySessionStart(
    props: {
      /** id of the session */
      sessionId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_session_start', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play session left
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlaySessionLeft(
    props: {
      /** id of the session */
      sessionId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_session_left', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play session join
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlaySessionJoin(
    props: {
      /** id of the session */
      sessionId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_session_join', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play button press
   * @param [extra] - extra properties for this event
   */
  sharePlayButtonPress(extra?: AnalyticEventProps) {
    return this.track('share_play_button_press', extra);
  }

  /**
   * Analytic event logged on share play showcase button press
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayShowcaseButtonPress(
    props?: {
      /** id of the session */
      sessionId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_showcase_button_press', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on share play wishlist add
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWishlistAdd(
    props: {
      /** id of the session */
      sessionId: string;
      /** id of the product */
      listingId: string;
      /** id of the show */
      showId: string;
      /** type of the show */
      showType: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_wishlist_add', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play wishlist remove
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWishlistRemove(
    props: {
      /** id of the session */
      sessionId: string;
      /** id of the product */
      listingId: string;
      /** id of the show */
      showId: string;
      /** type of the show */
      showType: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_wishlist_remove', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play wishlist thumbnail press
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWishlistThumbnailPress(
    props: {
      /** url of the link */
      linkUrl: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_wishlist_thumbnail_press', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on share play show thumbnail press
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayShowThumbnailPress(
    props: {
      /** id of the session */
      sessionId: string;
      /** url of the show */
      showUrl: string;
      /** id of the show */
      showId: string;
      /** type of the show */
      showType: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_show_thumbnail_press', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on share play waveover press
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWaveoverPress(
    props?: {
      /** id of the session */
      sessionId?: string | null;
      /** url of the show */
      showUrl?: string | null;
      /** id of the show */
      showId?: string | null;
      /** type of the show */
      showType?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_waveover_press', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play waveover success
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWaveoverSuccess(
    props?: {
      /** id of the session */
      sessionId?: string | null;
      /** url of the show */
      showUrl?: string | null;
      /** id of the show */
      showId?: string | null;
      /** type of the show */
      showType?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_waveover_success', { ...props, ...extra });
  }

  /**
   * Analytic event logged on share play waveover timeout
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  sharePlayWaveoverTimeout(
    props?: {
      /** id of the session */
      sessionId?: string | null;
      /** url of the show */
      showUrl?: string | null;
      /** id of the show */
      showId?: string | null;
      /** type of the show */
      showType?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('share_play_waveover_timeout', { ...props, ...extra });
  }

  //#endregion

  //#region Product Related Analytic Events

  /**
   * User pressed a listing
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  productPressed(
    props: {
      /** from where the product/listing is clicked */
      scene:
        | 'product_search_results'
        | 'featured_product'
        | 'profile_storefront'
        | 'video_clip_page'
        | 'order_review_page'
        | 'order_thank_you_page'
        | 'order_detail_item'
        | 'video_feed_page'
        | 'pop_store_digital_product_custom_button'
        | 'pop_store_my_store_collection';
      /** the type of the product */
      type:
        | 'liveShow'
        | 'storeFav'
        | 'videoClip'
        | 'orderReview'
        | 'linkInBio'
        | 'popStoreMyStore';
      /** type of the related show */
      relatedShowType?:
        | (
            | 'liveShow'
            | 'upcomingShow'
            | 'featuredLiveShow'
            | 'featuredUpcomingShow'
          )
        | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('product_pressed', { ...props, ...extra });
  }

  //#endregion

  //#region Guest checkout Related Analytic Events

  /**
   * Analytic event logged on guest checkout started
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutStarted(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_started', { ...props, ...extra });
  }

  /**
   * Analytic event logged on guest checkout order created
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutOrderCreated(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_order_created', { ...props, ...extra });
  }

  /**
   * Analytic event logged on guest checkout failed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutFailed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
      /** reason property */
      reason?: ('canceled' | 'sold_out') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_failed', { ...props, ...extra });
  }

  /**
   * Analytic event logged on guest checkout finished with account creation
   * successful
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutFinishedWithAccountCreationSuccessful(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_finished_with_account_creation_successful',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout finished with account creation
   * failure
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutFinishedWithAccountCreationFailure(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_finished_with_account_creation_failure', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout finished without account creation
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutFinishedWithoutAccountCreation(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_finished_without_account_creation', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout aborted
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutAborted(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_aborted', { ...props, ...extra });
  }

  /**
   * Analytic event logged on guest checkout post account creation successful
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationSuccessful(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_post_account_creation_successful', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout post account creation failed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationFailed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
      /** reason property */
      reason?: ('user_cancel' | 'error') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_post_account_creation_failed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout post account creation view account
   * pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationViewAccountPressed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_post_account_creation_view_account_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout post account creation continue
   * shopping pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationContinueShoppingPressed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_post_account_creation_continue_shopping_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout post account creation create
   * account pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationCreateAccountPressed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_post_account_creation_create_account_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout post account creation canceled
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPostAccountCreationCanceled(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_post_account_creation_canceled', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout phone number exists
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutPhoneNumberExists(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('guest_checkout_phone_number_exists', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on guest checkout continue with existing phone number
   * pressed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutContinueWithExistingPhoneNumberPressed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_continue_with_existing_phone_number_pressed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout continue with existing phone number
   * skipped
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutContinueWithExistingPhoneNumberSkipped(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_continue_with_existing_phone_number_skipped',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout continue with existing phone number
   * successful
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutContinueWithExistingPhoneNumberSuccessful(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_continue_with_existing_phone_number_successful',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout continue with existing phone number
   * failed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutContinueWithExistingPhoneNumberFailed(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_continue_with_existing_phone_number_failed',
      { ...props, ...extra },
    );
  }

  /**
   * Analytic event logged on guest checkout continue with existing phone number
   * canceled
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  guestCheckoutContinueWithExistingPhoneNumberCanceled(
    props?: {
      /** from_apple_pay property */
      fromApplePay?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'guest_checkout_continue_with_existing_phone_number_canceled',
      { ...props, ...extra },
    );
  }

  //#endregion

  //#region Seller Related Analytic Events

  /**
   * Analytic event logged on seller store reel posted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerStoreReelPosted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_store_reel_posted', { ...props, ...extra });
  }

  /**
   * Analytic event logged on seller dropship product imported
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerDropshipProductImported(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
      /** id of the commentsold dropship product */
      dropshipProductId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_dropship_product_imported', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller dropship product removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerDropshipProductRemoved(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
      /** id of the commentsold dropship product */
      dropshipProductId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_dropship_product_removed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller dropship product added to storefront
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerDropshipProductAddedToStorefront(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
      /** id of the commentsold dropship product */
      dropshipProductId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_dropship_product_added_to_storefront', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller dropship product removed from storefront
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerDropshipProductRemovedFromStorefront(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
      /** id of the commentsold dropship product */
      dropshipProductId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_dropship_product_removed_from_storefront', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller product added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerProductAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_product_added', { ...props, ...extra });
  }

  /**
   * Analytic event logged on seller product removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerProductRemoved(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_product_removed', { ...props, ...extra });
  }

  /**
   * Analytic event logged on seller product added to storefront
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerProductAddedToStorefront(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_product_added_to_storefront', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller product removed from storefront
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerProductRemovedFromStorefront(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_product_removed_from_storefront', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding dropship product added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingDropshipProductAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the psl dropship product */
      id: string;
      /** name of the product */
      name: string;
      /** id of the commentsold dropship product */
      dropshipProductId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_dropship_product_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding store bio added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingStoreBioAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_store_bio_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding store name added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingStoreNameAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_store_name_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding cover image added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingCoverImageAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_cover_image_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding categories selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingCategoriesSelected(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_categories_selected', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding business address added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingBusinessAddressAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_business_address_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding profile picture added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingProfilePictureAdded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_profile_picture_added', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding store reel posted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingStoreReelPosted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_store_reel_posted', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding profile completed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingProfileCompleted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_profile_completed', {
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on seller onboarding completed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  sellerOnboardingCompleted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('seller_onboarding_completed', { ...props, ...extra });
  }

  //#endregion

  //#region Commentchat Related Analytic Events

  /**
   * Analytic event logged on commentchat connected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatConnected(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_connected', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat disconnected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatDisconnected(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_disconnected', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat connection updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatConnectionUpdated(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_connection_updated', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat start
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatStart(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_start', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat created
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatCreated(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id property */
      id?: string | null;
      /** channel_id property */
      channelId?: string | null;
      /** channel_type property */
      channelType?: ('fb' | 'ig') | null;
      /** post_type property */
      postType?: ('next' | 'existing') | null;
      /** dest_type property */
      destType?: ('clip' | 'product' | 'custom_url') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_created', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat deleted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatDeleted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id property */
      id?: string | null;
      /** channel_id property */
      channelId?: string | null;
      /** channel_type property */
      channelType?: ('fb' | 'ig') | null;
      /** post_type property */
      postType?: ('next' | 'existing') | null;
      /** dest_type property */
      destType?: ('clip' | 'product' | 'custom_url') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_deleted', { ...props, ...extra });
  }

  /**
   * Analytic event logged on commentchat updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  commentchatUpdated(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id property */
      id?: string | null;
      /** channel_id property */
      channelId?: string | null;
      /** channel_type property */
      channelType?: ('fb' | 'ig') | null;
      /** post_type property */
      postType?: ('next' | 'existing') | null;
      /** dest_type property */
      destType?: ('clip' | 'product' | 'custom_url') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('commentchat_updated', { ...props, ...extra });
  }

  //#endregion

  //#region Popclip Related Analytic Events

  /**
   * Analytic event logged on popclip uploaded
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popclipUploaded(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the video clip */
      id: string;
      /** title of the video clip */
      title?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popclip_uploaded', { ...props, ...extra });
  }

  /**
   * Analytic event logged on popclip updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popclipUpdated(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the video clip */
      id: string;
      /** title of the video clip */
      title?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popclip_updated', { ...props, ...extra });
  }

  /**
   * Analytic event logged on popclip deleted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popclipDeleted(
    props: {
      /** id of the seller */
      sellerId: string;
      /** name of the seller */
      sellerName: string;
      /** id of the video clip */
      id: string;
      /** title of the video clip */
      title?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popclip_deleted', { ...props, ...extra });
  }

  //#endregion

  //#region Popstore Related Analytic Events

  /**
   * Analytic event logged on popstore payout configured
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstorePayoutConfigured(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_payout_configured', {
      elementAction: 'popstore_payout_configured',
      elementActionStage: 'popstore_payout_configured',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore payout updated
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstorePayoutUpdated(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_payout_updated', {
      elementAction: 'popstore_payout_updated',
      elementActionStage: 'popstore_payout_updated',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio header image uploaded
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioHeaderImageUploaded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_header_image_uploaded', {
      elementAction: 'popstore_bio_header_image_uploaded',
      elementActionStage: 'popstore_bio_header_image_uploaded',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio header image added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioHeaderImageAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_header_image_added', {
      elementAction: 'popstore_bio_header_image_added',
      elementActionStage: 'popstore_bio_header_image_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio header image updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioHeaderImageUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_header_image_updated', {
      elementAction: 'popstore_bio_header_image_updated',
      elementActionStage: 'popstore_bio_header_image_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio header image removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioHeaderImageRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_header_image_removed', {
      elementAction: 'popstore_bio_header_image_removed',
      elementActionStage: 'popstore_bio_header_image_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio profile picture added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioProfilePictureAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_profile_picture_added', {
      elementAction: 'popstore_bio_profile_picture_added',
      elementActionStage: 'popstore_bio_profile_picture_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio profile picture updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioProfilePictureUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_profile_picture_updated', {
      elementAction: 'popstore_bio_profile_picture_updated',
      elementActionStage: 'popstore_bio_profile_picture_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio profile picture removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioProfilePictureRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** image_url property */
      imageUrl: string;
      /** image_type property */
      imageType: 'png' | 'jpg' | 'gif' | 'hevc' | 'other';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_profile_picture_removed', {
      elementAction: 'popstore_bio_profile_picture_removed',
      elementActionStage: 'popstore_bio_profile_picture_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio link added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioLinkAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** button_type property */
      buttonType: 'text' | 'image' | 'video' | 'digital-product';
      /** button_heading property */
      buttonHeading?: string | null;
      /** button_sub_heading property */
      buttonSubHeading?: string | null;
      /** button_url property */
      buttonUrl?: string | null;
      /** button_text_alignment property */
      buttonTextAlignment?: string | null;
      /** button_text_vertical_alignment property */
      buttonTextVerticalAlignment?: string | null;
      /** button_text_color property */
      buttonTextColor?: string | null;
      /** button_sub_heading_color property */
      buttonSubHeadingColor?: string | null;
      /** button_border_color property */
      buttonBorderColor?: string | null;
      /** button_shape property */
      buttonShape?: string | null;
      /** button_is_hidden property */
      buttonIsHidden?: boolean | null;
      /** has_left_icon property */
      hasLeftIcon?: boolean | null;
      /** has_right_icon property */
      hasRightIcon?: boolean | null;
      /** button_animation property */
      buttonAnimation?: string | null;
      /** button_image_url property */
      buttonImageUrl?: string | null;
      /** button_video_url property */
      buttonVideoUrl?: string | null;
      /** button_product_id property */
      buttonProductId?: string | null;
      /** button_product_name property */
      buttonProductName?: string | null;
      /** button_product_price property */
      buttonProductPrice?: number | null;
      /** button_product_discount_price property */
      buttonProductDiscountPrice?: number | null;
      /** button_product_discount_price_enabled property */
      buttonProductDiscountPriceEnabled?: boolean | null;
      /** button_product_thumbnail property */
      buttonProductThumbnail?: string | null;
      /** button_overlay_shadow_opacity property */
      buttonOverlayShadowOpacity?: number | null;
      /** button_overlay_blur_effect property */
      buttonOverlayBlurEffect?: number | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_link_added', {
      elementAction: 'popstore_bio_link_added',
      elementActionStage: 'popstore_bio_link_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio link updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioLinkUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** button_type property */
      buttonType: 'text' | 'image' | 'video' | 'digital-product';
      /** button_heading property */
      buttonHeading?: string | null;
      /** button_sub_heading property */
      buttonSubHeading?: string | null;
      /** button_url property */
      buttonUrl?: string | null;
      /** button_text_alignment property */
      buttonTextAlignment?: string | null;
      /** button_text_vertical_alignment property */
      buttonTextVerticalAlignment?: string | null;
      /** button_text_color property */
      buttonTextColor?: string | null;
      /** button_sub_heading_color property */
      buttonSubHeadingColor?: string | null;
      /** button_border_color property */
      buttonBorderColor?: string | null;
      /** button_shape property */
      buttonShape?: string | null;
      /** button_is_hidden property */
      buttonIsHidden?: boolean | null;
      /** has_left_icon property */
      hasLeftIcon?: boolean | null;
      /** has_right_icon property */
      hasRightIcon?: boolean | null;
      /** button_animation property */
      buttonAnimation?: string | null;
      /** button_image_url property */
      buttonImageUrl?: string | null;
      /** button_video_url property */
      buttonVideoUrl?: string | null;
      /** button_product_id property */
      buttonProductId?: string | null;
      /** button_product_name property */
      buttonProductName?: string | null;
      /** button_product_price property */
      buttonProductPrice?: number | null;
      /** button_product_discount_price property */
      buttonProductDiscountPrice?: number | null;
      /** button_product_discount_price_enabled property */
      buttonProductDiscountPriceEnabled?: boolean | null;
      /** button_product_thumbnail property */
      buttonProductThumbnail?: string | null;
      /** button_overlay_shadow_opacity property */
      buttonOverlayShadowOpacity?: number | null;
      /** button_overlay_blur_effect property */
      buttonOverlayBlurEffect?: number | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_link_updated', {
      elementAction: 'popstore_bio_link_updated',
      elementActionStage: 'popstore_bio_link_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio link removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioLinkRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** button_type property */
      buttonType: 'text' | 'image' | 'video' | 'digital-product';
      /** button_heading property */
      buttonHeading?: string | null;
      /** button_sub_heading property */
      buttonSubHeading?: string | null;
      /** button_url property */
      buttonUrl?: string | null;
      /** button_text_alignment property */
      buttonTextAlignment?: string | null;
      /** button_text_vertical_alignment property */
      buttonTextVerticalAlignment?: string | null;
      /** button_text_color property */
      buttonTextColor?: string | null;
      /** button_sub_heading_color property */
      buttonSubHeadingColor?: string | null;
      /** button_border_color property */
      buttonBorderColor?: string | null;
      /** button_shape property */
      buttonShape?: string | null;
      /** button_is_hidden property */
      buttonIsHidden?: boolean | null;
      /** has_left_icon property */
      hasLeftIcon?: boolean | null;
      /** has_right_icon property */
      hasRightIcon?: boolean | null;
      /** button_animation property */
      buttonAnimation?: string | null;
      /** button_image_url property */
      buttonImageUrl?: string | null;
      /** button_video_url property */
      buttonVideoUrl?: string | null;
      /** button_product_id property */
      buttonProductId?: string | null;
      /** button_product_name property */
      buttonProductName?: string | null;
      /** button_product_price property */
      buttonProductPrice?: number | null;
      /** button_product_discount_price property */
      buttonProductDiscountPrice?: number | null;
      /** button_product_discount_price_enabled property */
      buttonProductDiscountPriceEnabled?: boolean | null;
      /** button_product_thumbnail property */
      buttonProductThumbnail?: string | null;
      /** button_overlay_shadow_opacity property */
      buttonOverlayShadowOpacity?: number | null;
      /** button_overlay_blur_effect property */
      buttonOverlayBlurEffect?: number | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_link_removed', {
      elementAction: 'popstore_bio_link_removed',
      elementActionStage: 'popstore_bio_link_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio social link added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioSocialLinkAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** social_media_url property */
      socialMediaUrl: string;
      /** link_is_hidden property */
      linkIsHidden?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_social_link_added', {
      elementAction: 'popstore_bio_social_link_added',
      elementActionStage: 'popstore_bio_social_link_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio social link updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioSocialLinkUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** social_media_url property */
      socialMediaUrl: string;
      /** link_is_hidden property */
      linkIsHidden?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_social_link_updated', {
      elementAction: 'popstore_bio_social_link_updated',
      elementActionStage: 'popstore_bio_social_link_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio social link removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioSocialLinkRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** social_media_url property */
      socialMediaUrl: string;
      /** link_is_hidden property */
      linkIsHidden?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_social_link_removed', {
      elementAction: 'popstore_bio_social_link_removed',
      elementActionStage: 'popstore_bio_social_link_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life series add button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSeriesAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_series_add_button_clicked', {
      elementAction: 'popstore_my_life_series_add_button_clicked',
      elementActionStage: 'popstore_my_life_series_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'series',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life series added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSeriesAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** series_id property */
      seriesId: string;
      /** series_name property */
      seriesName: string;
      /** show_labels property */
      showLabels?: boolean | null;
      /** JSON serialized array of selected videos w/ props: videoID, sequence_number, video_url */
      selectedVideos?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_series_added', {
      elementAction: 'popstore_my_life_series_added',
      elementActionStage: 'popstore_my_life_series_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'series',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life series updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSeriesUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** series_id property */
      seriesId: string;
      /** series_name property */
      seriesName: string;
      /** show_labels property */
      showLabels?: boolean | null;
      /** JSON serialized array of selected videos w/ props: videoID, sequence_number, video_url */
      selectedVideos?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_series_updated', {
      elementAction: 'popstore_my_life_series_updated',
      elementActionStage: 'popstore_my_life_series_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'series',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life series removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSeriesRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** series_id property */
      seriesId: string;
      /** series_name property */
      seriesName: string;
      /** show_labels property */
      showLabels?: boolean | null;
      /** JSON serialized array of selected videos w/ props: videoID, sequence_number, video_url */
      selectedVideos?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_series_removed', {
      elementAction: 'popstore_my_life_series_removed',
      elementActionStage: 'popstore_my_life_series_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'series',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life video uploaded
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeVideoUploaded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_id property */
      videoId: string;
      /** video_title property */
      videoTitle: string;
      /** video_text property */
      videoText?: string | null;
      /** add_to_series_enabled property */
      addToSeriesEnabled?: boolean | null;
      /** content_type property */
      contentType?: string | null;
      /** video_url property */
      videoUrl: string;
      /** video_file_size property */
      videoFileSize?: string | null;
      /** video_file_size_in_bytes property */
      videoFileSizeInBytes?: number | null;
      /** video_file_type property */
      videoFileType?: string | null;
      /** video_duration property */
      videoDuration?: number | null;
      /** associated_series property */
      associatedSeries?: string | null;
      /** access_level property */
      accessLevel?: ('all' | 'subscriber' | 'fan' | 'vip') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_video_uploaded', {
      elementAction: 'popstore_my_life_video_uploaded',
      elementActionStage: 'popstore_my_life_video_uploaded',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life video updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeVideoUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_id property */
      videoId: string;
      /** video_title property */
      videoTitle: string;
      /** video_text property */
      videoText?: string | null;
      /** add_to_series_enabled property */
      addToSeriesEnabled?: boolean | null;
      /** content_type property */
      contentType?: string | null;
      /** video_url property */
      videoUrl: string;
      /** video_file_size property */
      videoFileSize?: string | null;
      /** video_file_size_in_bytes property */
      videoFileSizeInBytes?: number | null;
      /** video_file_type property */
      videoFileType?: string | null;
      /** video_duration property */
      videoDuration?: number | null;
      /** associated_series property */
      associatedSeries?: string | null;
      /** access_level property */
      accessLevel?: ('all' | 'subscriber' | 'fan' | 'vip') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_video_updated', {
      elementAction: 'popstore_my_life_video_updated',
      elementActionStage: 'popstore_my_life_video_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life video deleted
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeVideoDeleted(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_id property */
      videoId: string;
      /** video_title property */
      videoTitle: string;
      /** video_text property */
      videoText?: string | null;
      /** add_to_series_enabled property */
      addToSeriesEnabled?: boolean | null;
      /** content_type property */
      contentType?: string | null;
      /** video_url property */
      videoUrl: string;
      /** video_file_size property */
      videoFileSize?: string | null;
      /** video_file_size_in_bytes property */
      videoFileSizeInBytes?: number | null;
      /** video_file_type property */
      videoFileType?: string | null;
      /** video_duration property */
      videoDuration?: number | null;
      /** associated_series property */
      associatedSeries?: string | null;
      /** access_level property */
      accessLevel?: ('all' | 'subscriber' | 'fan' | 'vip') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_video_deleted', {
      elementAction: 'popstore_my_life_video_deleted',
      elementActionStage: 'popstore_my_life_video_deleted',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life video added to series
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeVideoAddedToSeries(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_id property */
      videoId: string;
      /** video_title property */
      videoTitle: string;
      /** video_text property */
      videoText?: string | null;
      /** add_to_series_enabled property */
      addToSeriesEnabled?: boolean | null;
      /** content_type property */
      contentType?: string | null;
      /** video_url property */
      videoUrl: string;
      /** video_file_size property */
      videoFileSize?: string | null;
      /** video_file_size_in_bytes property */
      videoFileSizeInBytes?: number | null;
      /** video_file_type property */
      videoFileType?: string | null;
      /** video_duration property */
      videoDuration?: number | null;
      /** associated_series property */
      associatedSeries?: string | null;
      /** access_level property */
      accessLevel?: ('all' | 'subscriber' | 'fan' | 'vip') | null;
      /** series_id property */
      seriesId: string;
      /** series_name property */
      seriesName?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_video_added_to_series', {
      elementAction: 'popstore_my_life_video_added_to_series',
      elementActionStage: 'popstore_my_life_video_added_to_series',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life video removed from series
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeVideoRemovedFromSeries(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_id property */
      videoId: string;
      /** video_title property */
      videoTitle: string;
      /** video_text property */
      videoText?: string | null;
      /** add_to_series_enabled property */
      addToSeriesEnabled?: boolean | null;
      /** content_type property */
      contentType?: string | null;
      /** video_url property */
      videoUrl: string;
      /** video_file_size property */
      videoFileSize?: string | null;
      /** video_file_size_in_bytes property */
      videoFileSizeInBytes?: number | null;
      /** video_file_type property */
      videoFileType?: string | null;
      /** video_duration property */
      videoDuration?: number | null;
      /** associated_series property */
      associatedSeries?: string | null;
      /** access_level property */
      accessLevel?: ('all' | 'subscriber' | 'fan' | 'vip') | null;
      /** series_id property */
      seriesId: string;
      /** series_name property */
      seriesName?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_video_removed_from_series', {
      elementAction: 'popstore_my_life_video_removed_from_series',
      elementActionStage: 'popstore_my_life_video_removed_from_series',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life reel uploaded
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeReelUploaded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_url property */
      videoUrl: string;
      /** video_type property */
      videoType?: ('mp4' | 'mov' | 'other') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_reel_uploaded', {
      elementAction: 'popstore_my_life_reel_uploaded',
      elementActionStage: 'popstore_my_life_reel_uploaded',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life reel updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeReelUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_url property */
      videoUrl: string;
      /** video_type property */
      videoType?: ('mp4' | 'mov' | 'other') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_reel_updated', {
      elementAction: 'popstore_my_life_reel_updated',
      elementActionStage: 'popstore_my_life_reel_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life reel removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeReelRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** video_url property */
      videoUrl: string;
      /** video_type property */
      videoType?: ('mp4' | 'mov' | 'other') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_reel_removed', {
      elementAction: 'popstore_my_life_reel_removed',
      elementActionStage: 'popstore_my_life_reel_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'media',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life live scheduled
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeLiveScheduled(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** show_id property */
      showId: string;
      /** title property */
      title: string;
      /** permission property */
      permission?: ('all' | 'follower' | 'subscriber' | 'no-one') | null;
      /** subscription level of a subscription or content */
      subscriptionLevel?: ('subscriber' | 'fan' | 'vip') | null;
      /** starts_at property */
      startsAt: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_live_scheduled', {
      elementAction: 'popstore_my_life_live_scheduled',
      elementActionStage: 'popstore_my_life_live_scheduled',
      alsoSendToCsDatalake: true,
      elementCategory: 'live',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life live started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeLiveStarted(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** show_id property */
      showId: string;
      /** title property */
      title: string;
      /** permission property */
      permission?: ('all' | 'follower' | 'subscriber' | 'no-one') | null;
      /** subscription level of a subscription or content */
      subscriptionLevel?: ('subscriber' | 'fan' | 'vip') | null;
      /** upcoming_show_id property */
      upcomingShowId?: string | null;
      /** upcoming_show_title property */
      upcomingShowTitle?: string | null;
      /** upcoming_show_starts_at property */
      upcomingShowStartsAt?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_live_started', {
      elementAction: 'popstore_my_life_live_started',
      elementActionStage: 'popstore_my_life_live_started',
      alsoSendToCsDatalake: true,
      elementCategory: 'live',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life subscription added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSubscriptionAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** subscription level of a subscription or content */
      subscriptionLevel: 'subscriber' | 'fan' | 'vip';
      /** name of the subscription */
      name: string;
      /** price property */
      price: number;
      /** image_url property */
      imageUrl?: string | null;
      /** video_url property */
      videoUrl?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_subscription_added', {
      elementAction: 'popstore_my_life_subscription_added',
      elementActionStage: 'popstore_my_life_subscription_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'subscription',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life subscription updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSubscriptionUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** subscription level of a subscription or content */
      subscriptionLevel: 'subscriber' | 'fan' | 'vip';
      /** name of the subscription */
      name: string;
      /** price property */
      price: number;
      /** image_url property */
      imageUrl?: string | null;
      /** video_url property */
      videoUrl?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_subscription_updated', {
      elementAction: 'popstore_my_life_subscription_updated',
      elementActionStage: 'popstore_my_life_subscription_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'subscription',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my life subscription removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyLifeSubscriptionRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** subscription level of a subscription or content */
      subscriptionLevel: 'subscriber' | 'fan' | 'vip';
      /** name of the subscription */
      name: string;
      /** price property */
      price: number;
      /** image_url property */
      imageUrl?: string | null;
      /** video_url property */
      videoUrl?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_life_subscription_removed', {
      elementAction: 'popstore_my_life_subscription_removed',
      elementActionStage: 'popstore_my_life_subscription_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'subscription',
      ...props,
      ...extra,
    });
  }

  /**
   * sent when the user clicks check eligibility button
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDropshipEligibilityApplicationClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_dropship_eligibility_application_clicked',
      {
        elementAction:
          'popstore_my_store_dropship_eligibility_application_clicked',
        elementActionStage:
          'popstore_my_store_dropship_eligibility_application_clicked',
        alsoSendToCsDatalake: true,
        elementCategory: 'products',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * sent when the user successfully applies the dropship eligibility form
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDropshipEligibilityApplicationSubmitted(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_dropship_eligibility_application_submitted',
      {
        elementAction:
          'popstore_my_store_dropship_eligibility_application_submitted',
        elementActionStage:
          'popstore_my_store_dropship_eligibility_application_submitted',
        alsoSendToCsDatalake: true,
        elementCategory: 'products',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store product add button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreProductAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_product_add_button_clicked', {
      elementAction: 'popstore_my_store_product_add_button_clicked',
      elementActionStage: 'popstore_my_store_product_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store product imported
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreProductImported(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** product_name property */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_product_imported', {
      elementAction: 'popstore_my_store_product_imported',
      elementActionStage: 'popstore_my_store_product_imported',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store product removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreProductRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** product_name property */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_product_removed', {
      elementAction: 'popstore_my_store_product_removed',
      elementActionStage: 'popstore_my_store_product_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store product added to collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreProductAddedToCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** product_name property */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_product_added_to_collection', {
      elementAction: 'popstore_my_store_product_added_to_collection',
      elementActionStage: 'popstore_my_store_product_added_to_collection',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store product removed from collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreProductRemovedFromCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** product_name property */
      productName: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_product_removed_from_collection', {
      elementAction: 'popstore_my_store_product_removed_from_collection',
      elementActionStage: 'popstore_my_store_product_removed_from_collection',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product add button
   * clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_digital_product_add_button_clicked', {
      elementAction: 'popstore_my_store_digital_product_add_button_clicked',
      elementActionStage:
        'popstore_my_store_digital_product_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'digital-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId?: string | null;
      /** collection_name property */
      collectionName?: string | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_category property */
      productCategory?: string | null;
      /** product_description property */
      productDescription?: string | null;
      /** product_content_file property */
      productContentFile?: string | null;
      /** product_content_size property */
      productContentSize?: number | null;
      /** product_cover_image property */
      productCoverImage?: string | null;
      /** product_thumbnail_image property */
      productThumbnailImage?: string | null;
      /** product_price property */
      productPrice?: number | null;
      /** product_price_currency property */
      productPriceCurrency?: 'USD' | null;
      /** product_on_sale property */
      productOnSale?: boolean | null;
      /** product_sale_price_currency property */
      productSalePriceCurrency?: 'USD' | null;
      /** product_sale_start_date property */
      productSaleStartDate?: string | null;
      /** product_sale_end_date property */
      productSaleEndDate?: string | null;
      /** JSON serialized array of product embellishment image urls */
      productEmbellishmentImages?: string | null;
      /** product_embellishment_details property */
      productEmbellishmentDetails?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_digital_product_added', {
      elementAction: 'popstore_my_store_digital_product_added',
      elementActionStage: 'popstore_my_store_digital_product_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'digital-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId?: string | null;
      /** collection_name property */
      collectionName?: string | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_category property */
      productCategory?: string | null;
      /** product_description property */
      productDescription?: string | null;
      /** product_content_file property */
      productContentFile?: string | null;
      /** product_content_size property */
      productContentSize?: number | null;
      /** product_cover_image property */
      productCoverImage?: string | null;
      /** product_thumbnail_image property */
      productThumbnailImage?: string | null;
      /** product_price property */
      productPrice?: number | null;
      /** product_price_currency property */
      productPriceCurrency?: 'USD' | null;
      /** product_on_sale property */
      productOnSale?: boolean | null;
      /** product_sale_price_currency property */
      productSalePriceCurrency?: 'USD' | null;
      /** product_sale_start_date property */
      productSaleStartDate?: string | null;
      /** product_sale_end_date property */
      productSaleEndDate?: string | null;
      /** JSON serialized array of product embellishment image urls */
      productEmbellishmentImages?: string | null;
      /** product_embellishment_details property */
      productEmbellishmentDetails?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_digital_product_updated', {
      elementAction: 'popstore_my_store_digital_product_updated',
      elementActionStage: 'popstore_my_store_digital_product_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'digital-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId?: string | null;
      /** collection_name property */
      collectionName?: string | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_category property */
      productCategory?: string | null;
      /** product_description property */
      productDescription?: string | null;
      /** product_content_file property */
      productContentFile?: string | null;
      /** product_content_size property */
      productContentSize?: number | null;
      /** product_cover_image property */
      productCoverImage?: string | null;
      /** product_thumbnail_image property */
      productThumbnailImage?: string | null;
      /** product_price property */
      productPrice?: number | null;
      /** product_price_currency property */
      productPriceCurrency?: 'USD' | null;
      /** product_on_sale property */
      productOnSale?: boolean | null;
      /** product_sale_price_currency property */
      productSalePriceCurrency?: 'USD' | null;
      /** product_sale_start_date property */
      productSaleStartDate?: string | null;
      /** product_sale_end_date property */
      productSaleEndDate?: string | null;
      /** JSON serialized array of product embellishment image urls */
      productEmbellishmentImages?: string | null;
      /** product_embellishment_details property */
      productEmbellishmentDetails?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_digital_product_removed', {
      elementAction: 'popstore_my_store_digital_product_removed',
      elementActionStage: 'popstore_my_store_digital_product_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'digital-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product added to
   * collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductAddedToCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId?: string | null;
      /** collection_name property */
      collectionName?: string | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_category property */
      productCategory?: string | null;
      /** product_description property */
      productDescription?: string | null;
      /** product_content_file property */
      productContentFile?: string | null;
      /** product_content_size property */
      productContentSize?: number | null;
      /** product_cover_image property */
      productCoverImage?: string | null;
      /** product_thumbnail_image property */
      productThumbnailImage?: string | null;
      /** product_price property */
      productPrice?: number | null;
      /** product_price_currency property */
      productPriceCurrency?: 'USD' | null;
      /** product_on_sale property */
      productOnSale?: boolean | null;
      /** product_sale_price_currency property */
      productSalePriceCurrency?: 'USD' | null;
      /** product_sale_start_date property */
      productSaleStartDate?: string | null;
      /** product_sale_end_date property */
      productSaleEndDate?: string | null;
      /** JSON serialized array of product embellishment image urls */
      productEmbellishmentImages?: string | null;
      /** product_embellishment_details property */
      productEmbellishmentDetails?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_digital_product_added_to_collection', {
      elementAction: 'popstore_my_store_digital_product_added_to_collection',
      elementActionStage:
        'popstore_my_store_digital_product_added_to_collection',
      alsoSendToCsDatalake: true,
      elementCategory: 'digital-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store digital product removed from
   * collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreDigitalProductRemovedFromCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId?: string | null;
      /** collection_name property */
      collectionName?: string | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_category property */
      productCategory?: string | null;
      /** product_description property */
      productDescription?: string | null;
      /** product_content_file property */
      productContentFile?: string | null;
      /** product_content_size property */
      productContentSize?: number | null;
      /** product_cover_image property */
      productCoverImage?: string | null;
      /** product_thumbnail_image property */
      productThumbnailImage?: string | null;
      /** product_price property */
      productPrice?: number | null;
      /** product_price_currency property */
      productPriceCurrency?: 'USD' | null;
      /** product_on_sale property */
      productOnSale?: boolean | null;
      /** product_sale_price_currency property */
      productSalePriceCurrency?: 'USD' | null;
      /** product_sale_start_date property */
      productSaleStartDate?: string | null;
      /** product_sale_end_date property */
      productSaleEndDate?: string | null;
      /** JSON serialized array of product embellishment image urls */
      productEmbellishmentImages?: string | null;
      /** product_embellishment_details property */
      productEmbellishmentDetails?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_digital_product_removed_from_collection',
      {
        elementAction:
          'popstore_my_store_digital_product_removed_from_collection',
        elementActionStage:
          'popstore_my_store_digital_product_removed_from_collection',
        alsoSendToCsDatalake: true,
        elementCategory: 'digital-products',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store meeting slot google calendar
   * connected
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotGoogleCalendarConnected(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** if it is the first time the user connects their Google Calendar */
      isFirstConnection?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_meeting_slot_google_calendar_connected',
      {
        elementAction:
          'popstore_my_store_meeting_slot_google_calendar_connected',
        elementActionStage:
          'popstore_my_store_meeting_slot_google_calendar_connected',
        alsoSendToCsDatalake: true,
        elementCategory: 'meetings',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store meeting slot google calendar
   * disconnected
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotGoogleCalendarDisconnected(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_meeting_slot_google_calendar_disconnected',
      {
        elementAction:
          'popstore_my_store_meeting_slot_google_calendar_disconnected',
        elementActionStage:
          'popstore_my_store_meeting_slot_google_calendar_disconnected',
        alsoSendToCsDatalake: true,
        elementCategory: 'meetings',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store meeting slot add button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_meeting_slot_add_button_clicked', {
      elementAction: 'popstore_my_store_meeting_slot_add_button_clicked',
      elementActionStage: 'popstore_my_store_meeting_slot_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'meetings',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store meeting slot added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** meeting_id property */
      meetingId: string;
      /** meeting_datetime property */
      meetingDatetime: string;
      /** meeting duration in minutes */
      meetingDuration?: number | null;
      /** meeting_price property */
      meetingPrice?: number | null;
      /** meeting_topic property */
      meetingTopic?: string | null;
      /** meeting_attachment_file property */
      meetingAttachmentFile?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_meeting_slot_added', {
      elementAction: 'popstore_my_store_meeting_slot_added',
      elementActionStage: 'popstore_my_store_meeting_slot_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'meetings',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store meeting slot updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** meeting_id property */
      meetingId: string;
      /** meeting_datetime property */
      meetingDatetime: string;
      /** meeting duration in minutes */
      meetingDuration?: number | null;
      /** meeting_price property */
      meetingPrice?: number | null;
      /** meeting_topic property */
      meetingTopic?: string | null;
      /** meeting_attachment_file property */
      meetingAttachmentFile?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_meeting_slot_updated', {
      elementAction: 'popstore_my_store_meeting_slot_updated',
      elementActionStage: 'popstore_my_store_meeting_slot_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'meetings',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store meeting slot removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** meeting_id property */
      meetingId: string;
      /** meeting_datetime property */
      meetingDatetime: string;
      /** meeting duration in minutes */
      meetingDuration?: number | null;
      /** meeting_price property */
      meetingPrice?: number | null;
      /** meeting_topic property */
      meetingTopic?: string | null;
      /** meeting_attachment_file property */
      meetingAttachmentFile?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_meeting_slot_removed', {
      elementAction: 'popstore_my_store_meeting_slot_removed',
      elementActionStage: 'popstore_my_store_meeting_slot_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'meetings',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store external product add button
   * clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_external_product_add_button_clicked', {
      elementAction: 'popstore_my_store_external_product_add_button_clicked',
      elementActionStage:
        'popstore_my_store_external_product_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'external-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store external product added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_url property */
      productUrl: string;
      /** product_price property */
      productPrice?: number | null;
      /** product_image_url property */
      productImageUrl: string;
      /** product_description property */
      productDescription?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_external_product_added', {
      elementAction: 'popstore_my_store_external_product_added',
      elementActionStage: 'popstore_my_store_external_product_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'external-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store external product updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_url property */
      productUrl: string;
      /** product_price property */
      productPrice?: number | null;
      /** product_image_url property */
      productImageUrl: string;
      /** product_description property */
      productDescription?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_external_product_updated', {
      elementAction: 'popstore_my_store_external_product_updated',
      elementActionStage: 'popstore_my_store_external_product_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'external-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store external product removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_url property */
      productUrl: string;
      /** product_price property */
      productPrice?: number | null;
      /** product_image_url property */
      productImageUrl: string;
      /** product_description property */
      productDescription?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_external_product_removed', {
      elementAction: 'popstore_my_store_external_product_removed',
      elementActionStage: 'popstore_my_store_external_product_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'external-products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store external product added to
   * collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductAddedToCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_url property */
      productUrl: string;
      /** product_price property */
      productPrice?: number | null;
      /** product_image_url property */
      productImageUrl: string;
      /** product_description property */
      productDescription?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_external_product_added_to_collection',
      {
        elementAction: 'popstore_my_store_external_product_added_to_collection',
        elementActionStage:
          'popstore_my_store_external_product_added_to_collection',
        alsoSendToCsDatalake: true,
        elementCategory: 'external-products',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store external product removed from
   * collection
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreExternalProductRemovedFromCollection(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** type property */
      type?: ('EXTERNAL' | 'AFFILIATE') | null;
      /** product_id property */
      productId: string;
      /** product_name property */
      productName: string;
      /** product_url property */
      productUrl: string;
      /** product_price property */
      productPrice?: number | null;
      /** product_image_url property */
      productImageUrl: string;
      /** product_description property */
      productDescription?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_external_product_removed_from_collection',
      {
        elementAction:
          'popstore_my_store_external_product_removed_from_collection',
        elementActionStage:
          'popstore_my_store_external_product_removed_from_collection',
        alsoSendToCsDatalake: true,
        elementCategory: 'external-products',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore my store collection add button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreCollectionAddButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_collection_add_button_clicked', {
      elementAction: 'popstore_my_store_collection_add_button_clicked',
      elementActionStage: 'popstore_my_store_collection_add_button_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store collection added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreCollectionAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId: string;
      /** collection_name property */
      collectionName: string;
      /** collection_type property */
      collectionType?: ('manual_selection' | 'by_category') | null;
      /** JSON serialized array of product ids in the collection */
      collectionProductIds?: string | null;
      /** JSON serialized array of category ids of the collection */
      categoryIds?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_collection_added', {
      elementAction: 'popstore_my_store_collection_added',
      elementActionStage: 'popstore_my_store_collection_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store collection updated
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreCollectionUpdated(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId: string;
      /** collection_name property */
      collectionName: string;
      /** collection_type property */
      collectionType?: ('manual_selection' | 'by_category') | null;
      /** JSON serialized array of product ids in the collection */
      collectionProductIds?: string | null;
      /** JSON serialized array of category ids of the collection */
      categoryIds?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_collection_updated', {
      elementAction: 'popstore_my_store_collection_updated',
      elementActionStage: 'popstore_my_store_collection_updated',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store collection removed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreCollectionRemoved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** collection_id property */
      collectionId: string;
      /** collection_name property */
      collectionName: string;
      /** collection_type property */
      collectionType?: ('manual_selection' | 'by_category') | null;
      /** JSON serialized array of product ids in the collection */
      collectionProductIds?: string | null;
      /** JSON serialized array of category ids of the collection */
      categoryIds?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_my_store_collection_removed', {
      elementAction: 'popstore_my_store_collection_removed',
      elementActionStage: 'popstore_my_store_collection_removed',
      alsoSendToCsDatalake: true,
      elementCategory: 'products',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio page viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioPageViewed(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** creator_id property */
      creatorId: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_page_viewed', {
      elementAction: 'popstore_bio_page_viewed',
      elementActionStage: 'popstore_bio_page_viewed',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingStarted(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_started', {
      elementAction: 'popstore_onboarding_started',
      elementActionStage: 'popstore_onboarding_started',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding phone number entered
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingPhoneNumberEntered(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** phone_number property */
      phoneNumber: string;
      /** if the user has agreed to receive sms */
      receiveSmsChecked: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_phone_number_entered', {
      elementAction: 'popstore_onboarding_phone_number_entered',
      elementActionStage: 'popstore_onboarding_phone_number_entered',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding email password entered
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingEmailPasswordEntered(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** email property */
      email: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_email_password_entered', {
      elementAction: 'popstore_onboarding_email_password_entered',
      elementActionStage: 'popstore_onboarding_email_password_entered',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding verification code entered
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingVerificationCodeEntered(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_verification_code_entered', {
      elementAction: 'popstore_onboarding_verification_code_entered',
      elementActionStage: 'popstore_onboarding_verification_code_entered',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding login success
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingLoginSuccess(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** provider property */
      provider: 'phone' | 'email' | 'facebook.com' | 'google.com';
      /** whether the user is a new user */
      isNewUser: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_login_success', {
      elementAction: 'popstore_onboarding_login_success',
      elementActionStage: 'popstore_onboarding_login_success',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupStarted(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_started', {
      elementAction: 'popstore_onboarding_account_setup_started',
      elementActionStage: 'popstore_onboarding_account_setup_started',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup completed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupCompleted(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_completed', {
      elementAction: 'popstore_onboarding_account_setup_completed',
      elementActionStage: 'popstore_onboarding_account_setup_completed',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup custom domain
   * selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupCustomDomainSelected(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** domain property */
      domain: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_onboarding_account_setup_custom_domain_selected',
      {
        elementAction:
          'popstore_onboarding_account_setup_custom_domain_selected',
        elementActionStage:
          'popstore_onboarding_account_setup_custom_domain_selected',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore onboarding account setup submitting
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupSubmitting(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** the domain or custom domain for popstore creator or commentchat user, will be empty for general user */
      domain?: string | null;
      /** user entered username for popstore general user sign up, or placeholder username (e.g., psl_xxxx) for either creator or commentchat user */
      username?: string | null;
      /** for creator or commentchat user only, the user entered url code used in link in profile url, e.g., https://pop.store/andy */
      urlCode?: string | null;
      /** user entered first name, for general user or commentchat user only */
      firstName?: string | null;
      /** user entered last name, for general user or commentchat user only */
      lastName?: string | null;
      /** user entered email address, for general user or commentchat user only */
      email?: string | null;
      /** user entered instagram handle, for commentchat user only */
      igHandle?: string | null;
      /** user selected country code for billing */
      billingCountryCode: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_submitting', {
      elementAction: 'popstore_onboarding_account_setup_submitting',
      elementActionStage: 'popstore_onboarding_account_setup_submitting',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup failed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupFailed(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** action property */
      action: string;
      /** reason property */
      reason: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_failed', {
      elementAction: 'popstore_onboarding_account_setup_failed',
      elementActionStage: 'popstore_onboarding_account_setup_failed',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup avatar page
   * viewed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupAvatarPageViewed(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_avatar_page_viewed', {
      elementAction: 'popstore_onboarding_account_setup_avatar_page_viewed',
      elementActionStage:
        'popstore_onboarding_account_setup_avatar_page_viewed',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup avatar selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupAvatarSelected(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** type property */
      type: 'select_photo' | 'take_photo';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_avatar_selected', {
      elementAction: 'popstore_onboarding_account_setup_avatar_selected',
      elementActionStage: 'popstore_onboarding_account_setup_avatar_selected',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup avatar saved
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupAvatarSaved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_avatar_saved', {
      elementAction: 'popstore_onboarding_account_setup_avatar_saved',
      elementActionStage: 'popstore_onboarding_account_setup_avatar_saved',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup avatar done
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupAvatarDone(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_avatar_done', {
      elementAction: 'popstore_onboarding_account_setup_avatar_done',
      elementActionStage: 'popstore_onboarding_account_setup_avatar_done',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding account setup avatar skipped
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingAccountSetupAvatarSkipped(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_account_setup_avatar_skipped', {
      elementAction: 'popstore_onboarding_account_setup_avatar_skipped',
      elementActionStage: 'popstore_onboarding_account_setup_avatar_skipped',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding success
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingSuccess(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_success', {
      elementAction: 'popstore_onboarding_success',
      elementActionStage: 'popstore_onboarding_success',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding done button pressed
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingDoneButtonPressed(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** session_id property */
      sessionId: string;
      /** reg property */
      reg: 'creator' | 'commentchat' | 'user';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_done_button_pressed', {
      elementAction: 'popstore_onboarding_done_button_pressed',
      elementActionStage: 'popstore_onboarding_done_button_pressed',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding contest initialized
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestInitialized(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_contest_initialized', {
      elementAction: 'popstore_onboarding_contest_initialized',
      elementActionStage: 'popstore_onboarding_contest_initialized',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding contest bootstrapped
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestBootstrapped(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_contest_bootstrapped', {
      elementAction: 'popstore_onboarding_contest_bootstrapped',
      elementActionStage: 'popstore_onboarding_contest_bootstrapped',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding contest submitted
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestSubmitted(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_contest_submitted', {
      elementAction: 'popstore_onboarding_contest_submitted',
      elementActionStage: 'popstore_onboarding_contest_submitted',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding contest view leaderboard
   * button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestViewLeaderboardButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_onboarding_contest_view_leaderboard_button_clicked',
      {
        elementAction:
          'popstore_onboarding_contest_view_leaderboard_button_clicked',
        elementActionStage:
          'popstore_onboarding_contest_view_leaderboard_button_clicked',
        alsoSendToCsDatalake: true,
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore onboarding contest task cta button
   * clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestTaskCtaButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_onboarding_contest_task_cta_button_clicked', {
      elementAction: 'popstore_onboarding_contest_task_cta_button_clicked',
      elementActionStage: 'popstore_onboarding_contest_task_cta_button_clicked',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore onboarding contest leaderboard current
   * week entry bio clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestLeaderboardCurrentWeekEntryBioClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_onboarding_contest_leaderboard_current_week_entry_bio_clicked',
      {
        elementAction:
          'popstore_onboarding_contest_leaderboard_current_week_entry_bio_clicked',
        elementActionStage:
          'popstore_onboarding_contest_leaderboard_current_week_entry_bio_clicked',
        alsoSendToCsDatalake: true,
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore onboarding contest leaderboard winner bio
   * clicked
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestLeaderboardWinnerBioClicked(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** from property */
      from: 'leaderboard' | 'winners_gallery';
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_onboarding_contest_leaderboard_winner_bio_clicked',
      {
        elementAction:
          'popstore_onboarding_contest_leaderboard_winner_bio_clicked',
        elementActionStage:
          'popstore_onboarding_contest_leaderboard_winner_bio_clicked',
        alsoSendToCsDatalake: true,
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore onboarding contest leaderboard view
   * winners gallery button clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreOnboardingContestLeaderboardViewWinnersGalleryButtonClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_onboarding_contest_leaderboard_view_winners_gallery_button_clicked',
      {
        elementAction:
          'popstore_onboarding_contest_leaderboard_view_winners_gallery_button_clicked',
        elementActionStage:
          'popstore_onboarding_contest_leaderboard_view_winners_gallery_button_clicked',
        alsoSendToCsDatalake: true,
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore express setup entered
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupEntered(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_entered', {
      elementAction: 'popstore_express_setup_entered',
      elementActionStage: 'popstore_express_setup_entered',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup social links skipped
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupSocialLinksSkipped(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_social_links_skipped', {
      elementAction: 'popstore_express_setup_social_links_skipped',
      elementActionStage: 'popstore_express_setup_social_links_skipped',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup social links added
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupSocialLinksAdded(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** instagram_url property */
      instagramUrl: string;
      /** tiktok_url property */
      tiktokUrl: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_social_links_added', {
      elementAction: 'popstore_express_setup_social_links_added',
      elementActionStage: 'popstore_express_setup_social_links_added',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup profile info skipped
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupProfileInfoSkipped(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_profile_info_skipped', {
      elementAction: 'popstore_express_setup_profile_info_skipped',
      elementActionStage: 'popstore_express_setup_profile_info_skipped',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup profile info avatar
   * uploaded
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupProfileInfoAvatarUploaded(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_profile_info_avatar_uploaded', {
      elementAction: 'popstore_express_setup_profile_info_avatar_uploaded',
      elementActionStage: 'popstore_express_setup_profile_info_avatar_uploaded',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup profile info basic info
   * saved
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupProfileInfoBasicInfoSaved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** store_name property */
      storeName: string;
      /** bio property */
      bio: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_profile_info_basic_info_saved', {
      elementAction: 'popstore_express_setup_profile_info_basic_info_saved',
      elementActionStage:
        'popstore_express_setup_profile_info_basic_info_saved',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup purposes skipped
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupPurposesSkipped(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_purposes_skipped', {
      elementAction: 'popstore_express_setup_purposes_skipped',
      elementActionStage: 'popstore_express_setup_purposes_skipped',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup purposes saved
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupPurposesSaved(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** selected_values property */
      selectedValues: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_purposes_saved', {
      elementAction: 'popstore_express_setup_purposes_saved',
      elementActionStage: 'popstore_express_setup_purposes_saved',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore express setup done
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreExpressSetupDone(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_express_setup_done', {
      elementAction: 'popstore_express_setup_done',
      elementActionStage: 'popstore_express_setup_done',
      alsoSendToCsDatalake: true,
      elementCategory: 'express_setup',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore theme picker presented
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreThemePickerPresented(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** is_first_presentation property */
      isFirstPresentation: boolean;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_theme_picker_presented', {
      elementAction: 'popstore_theme_picker_presented',
      elementActionStage: 'popstore_theme_picker_presented',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore theme picker closed
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreThemePickerClosed(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_theme_picker_closed', {
      elementAction: 'popstore_theme_picker_closed',
      elementActionStage: 'popstore_theme_picker_closed',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore theme selected
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreThemeSelected(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** id property */
      id: string;
      /** name property */
      name: string;
      /** style property */
      style: string;
      /** type property */
      type: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_theme_selected', {
      elementAction: 'popstore_theme_selected',
      elementActionStage: 'popstore_theme_selected',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore theme applied
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreThemeApplied(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** id property */
      id: string;
      /** name property */
      name: string;
      /** style property */
      style: string;
      /** type property */
      type: string;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_theme_applied', {
      elementAction: 'popstore_theme_applied',
      elementActionStage: 'popstore_theme_applied',
      alsoSendToCsDatalake: true,
      ...props,
      ...extra,
    });
  }

  //#endregion

  //#region Live show watch started Related Analytic Events

  /**
   * Analytic event logged on live show watch started
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  liveShowWatchStarted(
    props: {
      /** id of the live show */
      showId: string;
      /** title of the live show */
      title: string;
      /** user id of host of the live show */
      userId: string;
      /** nick name of host of the live show */
      userName: string;
      /** id of the buyer */
      buyerId?: string | null;
      /** name of the buyer */
      buyerName?: string | null;
      /** used to track users' behavior when they are anonymous */
      linkId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('live_show_watch_started', { ...props, ...extra });
  }

  //#endregion
}

/**
 * Convert the given event props to the canonical form so that the property
 * keys all take the snake_cased form in order to keep them consistent with
 * their original definition in the schema configuration file.
 * @param [props] - the event props to convert
 * @returns converted props with canonical property keys
 */
export function eventPropsConverted(props: AnalyticEventProps | undefined) {
  if (!props) {
    return props;
  }
  const transformedProps: typeof props = {};
  for (const p in props) {
    const key = p
      .replace(
        /([^a-z])([a-z]*)/g,
        (_, l, t, i) =>
          (i ? '_' : '') + (l >= 'A' && l <= 'Z' ? l.toLowerCase() : l) + t,
      )
      .replace(/_{2,}/g, '_')
      .replace(/^_/, '')
      .replace(/_$/, '');
    transformedProps[key] = props[p];
  }
  return transformedProps;
}
